import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import api, { resolveApiErrorMessage } from '../../services/axios';
import { ENDPOINTS } from '../../config/endpoints';
import { useAuth } from '../../context/AuthContext';

function CompanyTemplatePreviewModal({ template, onClose }) {
  if (!template) return null;
  return createPortal(
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px 12px', paddingLeft: 'calc(var(--app-sidebar-width, 0px) + 12px)', overflowY: 'auto' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: 'rgba(0,0,0,0.55)', position: 'fixed', inset: 0 }} onClick={onClose} />
      <div style={{ width: '100%', maxWidth: 860, background: 'var(--ds-card)', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.4)', overflow: 'hidden', position: 'relative' }}>
        <div style={{ padding: '12px 16px', background: 'linear-gradient(135deg,#0041C2,#1A5FD4)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#fff' }}>{template.name} — Template Preview</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.78)', maxWidth: 640 }}>
              {template.version ? `Company Template (v${template.version})` : 'Company Template'}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: 28, height: 28, borderRadius: 6, cursor: 'pointer', outline: 'none' }}>
            <i className="fas fa-times" />
          </button>
        </div>
        <div style={{ padding: 14, background: '#eef1f5' }}>
          <iframe
            title={`company-template-preview-${template.id}`}
            srcDoc={template.preview_html || '<p style="padding:24px;text-align:center;color:#666;">No visual preview available for this template.</p>'}
            sandbox="allow-same-origin allow-scripts"
            style={{ width: '100%', height: '58vh', border: 'none', borderRadius: 8, background: '#fff' }}
          />
        </div>
        <div style={{ padding: '10px 16px', borderTop: '1px solid var(--ds-border)', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '7px 14px', fontSize: 12, fontWeight: 700, borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', border: '1.5px solid var(--ds-border)', background: 'var(--ds-input-bg)', color: 'var(--ds-text)' }}>
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

const MAX_FILE_MB = 200;
const ACCEPT = '.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const ptNum = (v) => (typeof v === 'number' ? Math.round(v * 10) / 10 : v);

function InfoRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '4px 0', borderBottom: '1px dashed var(--ds-border)' }}>
      <span style={{ fontSize: 11.5, color: 'var(--ds-text2)', flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--ds-text)', textAlign: 'right', wordBreak: 'break-word' }}>{value || '—'}</span>
    </div>
  );
}

function DetectedTemplateCard({ draft }) {
  const { detected, template } = draft || {};
  const typo = detected?.typography || {};
  const page = detected?.page || {};
  const margins = page.margins_pt || template?.page?.margins_pt || {};
  const logoAssetId = useMemo(() => {
    const fe = (template?.fixed_elements || []).find((e) => e.role === 'company_logo');
    if (fe && template?.assets?.[fe.asset]) return fe.asset;
    const v2Logo = template?.header?.logo?.asset_id; // dynamic v2 DSL
    if (v2Logo && template?.assets?.[v2Logo]) return v2Logo;
    return null;
  }, [template]);
  const logoEntry = logoAssetId ? template.assets[logoAssetId] : null;
  const logoSrc = logoEntry ? `data:${logoEntry.mime};base64,${logoEntry.data_b64}` : null;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 12 }}>
      {/* Typography */}
      <div style={{ background: 'var(--ds-input-bg)', border: '1px solid var(--ds-border)', borderRadius: 10, padding: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
          <i className="fas fa-font" style={{ color: '#1A5FD4', fontSize: 12 }} />
          <span style={{ fontWeight: 800, fontSize: 12, color: 'var(--ds-text)' }}>Typography</span>
        </div>
        <InfoRow label="Base font" value={typo.base_font} />
        <InfoRow label="Name size" value={typo.name_size_pt ? `${ptNum(typo.name_size_pt)}pt bold` : null} />
        <InfoRow label="Section heading" value={typo.heading_size_pt ? `${ptNum(typo.heading_size_pt)}pt` : null} />
        <InfoRow label="Body" value={typo.body_size_pt ? `${ptNum(typo.body_size_pt)}pt` : null} />
      </div>

      {/* Layout */}
      <div style={{ background: 'var(--ds-input-bg)', border: '1px solid var(--ds-border)', borderRadius: 10, padding: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
          <i className="fas fa-ruler-combined" style={{ color: '#1A5FD4', fontSize: 12 }} />
          <span style={{ fontWeight: 800, fontSize: 12, color: 'var(--ds-text)' }}>Layout</span>
        </div>
        <InfoRow label="Page" value={`${ptNum(page.width_pt)} × ${ptNum(page.height_pt)} pt`} />
        <InfoRow label="Orientation" value={page.orientation} />
        <InfoRow label="Margins T/R/B/L" value={`${ptNum(margins.top)} / ${ptNum(margins.right)} / ${ptNum(margins.bottom)} / ${ptNum(margins.left)} pt`} />
        <InfoRow label="Extraction" value={detected?.extraction_mode === 'ocr_inferred' ? 'OCR (inferred styles)' : 'Native'} />
      </div>

      {/* Sections */}
      <div style={{ background: 'var(--ds-input-bg)', border: '1px solid var(--ds-border)', borderRadius: 10, padding: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
          <i className="fas fa-list-ol" style={{ color: '#1A5FD4', fontSize: 12 }} />
          <span style={{ fontWeight: 800, fontSize: 12, color: 'var(--ds-text)' }}>Sections</span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {(detected?.sections || []).map((s) => (
            <span key={`${s.id}`} title={s.repeatable ? 'Repeatable section' : 'Section'} style={{
              padding: '3px 9px', borderRadius: 20, fontSize: 10.5, fontWeight: 700,
              background: s.repeatable ? 'rgba(0,65,194,0.12)' : 'var(--ds-card)',
              color: s.repeatable ? '#0041C2' : 'var(--ds-text)',
              border: `1px solid ${s.repeatable ? 'rgba(0,65,194,0.35)' : 'var(--ds-border)'}`,
            }}>
              {s.label || s.id}{s.repeatable && ' ↻'}
            </span>
          ))}
        </div>
        {detected?.repeatable?.length > 0 && (
          <div style={{ marginTop: 9, fontSize: 10.5, color: 'var(--ds-text2)' }}>
            Repeatable entries expand to fit each employee&apos;s content.
          </div>
        )}
      </div>

      {/* Assets */}
      <div style={{ background: 'var(--ds-input-bg)', border: '1px solid var(--ds-border)', borderRadius: 10, padding: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
          <i className="fas fa-image" style={{ color: '#1A5FD4', fontSize: 12 }} />
          <span style={{ fontWeight: 800, fontSize: 12, color: 'var(--ds-text)' }}>Assets / Logo</span>
        </div>
        {logoSrc ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 74, height: 46, borderRadius: 6, border: '1px solid var(--ds-border)', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
              <img src={logoSrc} alt="Company logo" style={{ maxWidth: '92%', maxHeight: '88%', objectFit: 'contain' }} />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 11.5, color: '#16a34a' }}>Company logo detected</div>
              <div style={{ fontSize: 10.5, color: 'var(--ds-text2)', marginTop: 2 }}>
                Source: {detected?.logo_source === 'vector_crop' ? 'vector graphic crop' : detected?.logo_source === 'docx_header' ? 'DOCX header' : 'embedded image'}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 11.5, color: 'var(--ds-text2)' }}>
            No extractable company logo found in the sample.
          </div>
        )}
        <div style={{ marginTop: 8 }}>
          <InfoRow label="Fixed elements" value={(template?.fixed_elements || []).length + ((template?.header_rules || []).length ? ` (+${template.header_rules.length} rule${template.header_rules.length > 1 ? 's' : ''})` : '')} />
        </div>
      </div>
    </div>
  );
}

/**
 * "Attach Your Own Resume Template" flow.
 * Lets the user pick a previously saved company template or extract + save a
 * new one from a sample resume. The chosen template (id, name, version and the
 * resolved standardize endpoint) is reported to the parent via onTemplateSelect.
 * When `embedded` is true it renders borderless so it can live inside the
 * parent's single Format Resume card.
 */
const OwnTemplateFlow = ({ onTemplateSelect, embedded = false }) => {
  const { user } = useAuth();

  // Saved templates
  const [templates, setTemplates] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [resolving, setResolving] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Extract-new-template flow state (existing dynamic extraction, unchanged)
  const sampleInputRef = useRef(null);
  const [sampleFile, setSampleFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [draft, setDraft] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [engine, setEngine] = useState('v1');
  const [saving, setSaving] = useState(false);
  const [showExtract, setShowExtract] = useState(false);

  const validateFile = (f, kindLabel) => {
    if (!f) return 'Please select a file.';
    const name = (f.name || '').toLowerCase();
    const extOk = /\.(pdf|doc|docx)$/.test(name) || /pdf|msword|wordprocessingml/.test(f.type || '');
    if (!extOk) return `${kindLabel} must be a PDF, DOC or DOCX file.`;
    if (f.size > MAX_FILE_MB * 1024 * 1024) return `${kindLabel} exceeds the ${MAX_FILE_MB} MB limit.`;
    return null;
  };

  /** Route the standardize endpoint by the template's DSL (v1 classic / v2 / v3) — probed once per selection. */
  const resolveStandardizeEndpoint = async (id) => {
    try {
      const meta = await api.get(ENDPOINTS.RESUME_TEMPLATES.GET_BY_ID(id), { timeout: 30000 });
      if (meta?.data?.template?.dsl === 'hirecrux_company_template') {
        // DSL v3.x routes to the v3 standardize endpoint; older dynamic
        // (v2) templates keep using the v2 endpoint unchanged.
        const ver = String(meta?.data?.template?.version || '');
        return ver.startsWith('3')
          ? ENDPOINTS.RESUME_TEMPLATES.STANDARDIZE_V3(id)
          : ENDPOINTS.RESUME_TEMPLATES.STANDARDIZE_V2(id);
      }
    } catch (metaErr) {
      console.warn('Template engine probe failed, using classic standardize:', metaErr);
    }
    return ENDPOINTS.RESUME_TEMPLATES.STANDARDIZE(id);
  };

  const reportSelection = async (id, list) => {
    if (!id) {
      setSelectedTemplateId('');
      onTemplateSelect(null);
      return;
    }
    setSelectedTemplateId(id);
    setResolving(true);
    try {
      const meta = (list || templates).find((t) => String(t.id) === String(id));
      const endpoint = await resolveStandardizeEndpoint(id);
      onTemplateSelect({ id, name: meta?.name || 'Company template', version: meta?.version ?? null, endpoint });
    } finally {
      setResolving(false);
    }
  };

  const loadTemplates = async () => {
    setLoadingList(true);
    try {
      const resp = await api.get(ENDPOINTS.RESUME_TEMPLATES.LIST, { timeout: 30000 });
      const list = Array.isArray(resp.data) ? resp.data : [];
      setTemplates(list);
      // Keep the current selection if still valid; otherwise auto-select the newest one.
      const nextId = selectedTemplateId && list.some((t) => String(t.id) === String(selectedTemplateId))
        ? selectedTemplateId
        : (list.length ? String(list[0].id) : '');
      await reportSelection(nextId, list);
      return list;
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error(resolveApiErrorMessage(error, 'Could not load saved templates.'));
      return [];
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => { loadTemplates(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const handlePreviewSavedTemplate = async () => {
    if (!selectedTemplateId) return;
    setPreviewLoading(true);
    try {
      // 1. If currently in memory from recent extraction
      if (draft?.preview_html && (String(draft?.savedId) === String(selectedTemplateId) || draft?.savedName === selectedTemplate?.name)) {
        setPreviewTemplate({
          id: selectedTemplateId,
          name: selectedTemplate?.name || 'Company Template',
          version: selectedTemplate?.version,
          preview_html: draft.preview_html,
        });
        return;
      }

      // 2. Fetch full template details from GET /resumes/templates/{id}
      const resp = await api.get(ENDPOINTS.RESUME_TEMPLATES.GET_BY_ID(selectedTemplateId), { timeout: 30000 });
      const tplData = resp?.data;
      const tplJson = tplData?.template;
      const html = tplJson?.preview_html || tplData?.preview_html;

      setPreviewTemplate({
        id: selectedTemplateId,
        name: tplData?.name || selectedTemplate?.name || 'Company Template',
        version: tplData?.version || selectedTemplate?.version,
        preview_html: html || '<p style="padding:24px;text-align:center;color:#666;">No visual preview available for this template.</p>',
      });
    } catch (err) {
      console.error('Error fetching template preview:', err);
      toast.error(resolveApiErrorMessage(err, 'Failed to load template preview.'));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSamplePick = (e) => {
    const f = e.target.files && e.target.files[0];
    const err = validateFile(f, 'Sample resume');
    if (err) {
      toast.error(err);
      e.target.value = '';
      return;
    }
    setSampleFile(f);
    setDraft(null);
    setTemplateName('');
  };

  const handleExtract = async () => {
    if (!sampleFile) { toast.error('Please select a sample resume first.'); return; }
    setExtracting(true);
    setDraft(null);
    try {
      const fd = new FormData();
      fd.append('file', sampleFile);
      const resp = await api.post(
        engine === 'v3' ? ENDPOINTS.RESUME_TEMPLATES.EXTRACT_V3
          : engine === 'v2' ? ENDPOINTS.RESUME_TEMPLATES.EXTRACT_V2
          : ENDPOINTS.RESUME_TEMPLATES.EXTRACT,
        fd, { timeout: 900000 });
      const data = resp?.data;
      if (!data?.template || !data?.preview_html) {
        toast.error('Template extraction returned an empty result. Please check the sample and retry.');
        return;
      }
      setDraft(data);
      if (!templateName) {
        const guess = (sampleFile.name || '').replace(/\.[^.]+$/, '').replace(/[_-]*sample.*$/i, '');
        setTemplateName(guess || 'Company Resume Template');
      }
      toast.success('Template extracted! Review it below before saving.');
    } catch (error) {
      console.error('Error extracting template:', error);
      const status = error?.response?.status;
      const msg = resolveApiErrorMessage(error, 'Template extraction failed. Please try again.');
      if (status === 400 || status === 504 || status === 502) toast.error(msg);
      else if (status === 401) { toast.error('Session expired. Please login again.'); setTimeout(() => { window.location.href = '/login'; }, 800); }
      else toast.error(msg);
    } finally {
      setExtracting(false);
    }
  };

  const handleSave = async () => {
    if (!draft?.template) { toast.error('Extract a template first.'); return; }
    if (!templateName.trim()) { toast.error('Please give this template a name.'); return; }
    setSaving(true);
    try {
      const resp = await api.post(ENDPOINTS.RESUME_TEMPLATES.SAVE, {
        name: templateName.trim(),
        template: {
          ...draft.template,
          preview_html: draft.preview_html,
        },
        createdBy: user?.email || '',
      }, { timeout: 120000 });
      toast.success(`Saved "${resp?.data?.name}" (v${resp?.data?.version}).`);
      const savedId = resp?.data?.id;
      setDraft((d) => (d ? { ...d, savedId, savedName: resp?.data?.name } : null));
      setSampleFile(null);
      setTemplateName('');
      setShowExtract(false);
      if (sampleInputRef.current) sampleInputRef.current.value = '';
      const list = await loadTemplates();
      // Auto-select the freshly saved template so the user can immediately format with it.
      const targetId = savedId
        ? String(savedId)
        : (list.find((t) => t.name === resp?.data?.name && String(t.version) === String(resp?.data?.version))?.id
          || list[0]?.id || '');
      if (targetId) await reportSelection(targetId, list);
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error(resolveApiErrorMessage(error, 'Failed to save the template.'));
    } finally {
      setSaving(false);
    }
  };

  const primaryBtn = { padding: '9px 24px', fontSize: 13, fontWeight: 700, borderRadius: 8, border: 'none', cursor: 'pointer', fontFamily: 'inherit', background: 'linear-gradient(135deg,#0041C2,#1A5FD4)', color: '#fff', boxShadow: '0 4px 14px rgba(0,65,194,0.35)', display: 'inline-flex', alignItems: 'center', gap: 8 };

  const selectedTemplate = templates.find((t) => String(t.id) === String(selectedTemplateId)) || null;

  const renderBody = () => (
    <>
        {/* Saved company template selector */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontWeight: 700, fontSize: 12, color: 'var(--ds-text)', marginBottom: 6 }}>Your Company Templates</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <select
              value={selectedTemplateId}
              onChange={(e) => reportSelection(e.target.value)}
              disabled={loadingList || resolving}
              style={{ flex: 1, minWidth: 0, padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--ds-border)', background: 'var(--ds-input-bg)', color: 'var(--ds-text)', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' }}
            >
              <option value="">{loadingList ? 'Loading templates...' : templates.length ? 'Select a company template' : 'No saved templates yet — extract one below'}</option>
              {templates.map((t) => (
                <option key={t.id} value={String(t.id)}>
                  {t.name}{t.version ? ` — v${t.version}` : ''}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={handlePreviewSavedTemplate}
              disabled={!selectedTemplate || previewLoading}
              title="Preview selected template"
              style={{ padding: '8px 11px', borderRadius: 8, cursor: selectedTemplate && !previewLoading ? 'pointer' : 'not-allowed', background: 'var(--ds-card)', color: '#1A5FD4', border: '1px solid var(--ds-border)', fontSize: 13, outline: 'none', flexShrink: 0 }}
            >
              <i className={previewLoading ? "fas fa-spinner fa-spin" : "fas fa-eye"} />
            </button>
          </div>
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 14 }}>
            <button onClick={loadTemplates} disabled={loadingList || resolving} style={{ background: 'transparent', border: 'none', color: '#1A5FD4', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', padding: 0, outline: 'none' }}>
              <i className={`fas fa-sync-alt ${loadingList ? 'fa-spin' : ''} me-1`} />Refresh list
            </button>
            <button onClick={() => setShowExtract((s) => !s)} style={{ background: 'transparent', border: 'none', color: '#1A5FD4', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', padding: 0, outline: 'none' }}>
              <i className={`fas ${showExtract ? 'fa-minus-circle' : 'fa-plus-circle'} me-1`} />{showExtract ? 'Hide extract new template' : 'Extract a new template from a sample'}
            </button>
          </div>
          {selectedTemplate && !resolving && (
            <div style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 8, padding: '5px 12px', borderRadius: 20, background: 'rgba(0,65,194,0.08)', border: '1px solid rgba(0,65,194,0.3)' }}>
              <i className="fas fa-building" style={{ color: '#0041C2', fontSize: 11 }} />
              <span style={{ fontSize: 11.5, fontWeight: 700, color: '#0041C2' }}>
                Selected: {selectedTemplate.name}{selectedTemplate.version ? ` — v${selectedTemplate.version}` : ''}
              </span>
            </div>
          )}
          {resolving && (
            <div style={{ marginTop: 8, fontSize: 11, color: 'var(--ds-text2)' }}>
              <i className="fas fa-sync-alt fa-spin me-1" />Resolving template engine...
            </div>
          )}
        </div>

        {/* Extract-new-template flow (collapsible; existing dynamic extraction, unchanged) */}
        {showExtract && (
          <>
            <div style={{ borderTop: '1px solid var(--ds-border)', margin: '4px 0 16px' }} />
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--ds-text)', marginBottom: 6 }}>Extraction Engine</div>
              <div style={{ display: 'inline-flex', border: '1.5px solid var(--ds-border)', borderRadius: 8, overflow: 'hidden' }}>
                <button type="button" onClick={() => setEngine('v1')} disabled={extracting}
                  style={{ padding: '7px 16px', fontSize: 12, fontWeight: 700, border: 'none', cursor: 'pointer', fontFamily: 'inherit', outline: 'none', background: engine === 'v1' ? 'linear-gradient(135deg,#0041C2,#1A5FD4)' : 'var(--ds-input-bg)', color: engine === 'v1' ? '#fff' : 'var(--ds-text)' }}>
                  Classic (v1)
                </button>
                <button type="button" onClick={() => setEngine('v2')} disabled={extracting}
                  style={{ padding: '7px 16px', fontSize: 12, fontWeight: 700, border: 'none', cursor: 'pointer', fontFamily: 'inherit', outline: 'none', background: engine === 'v2' ? 'linear-gradient(135deg,#0041C2,#1A5FD4)' : 'var(--ds-input-bg)', color: engine === 'v2' ? '#fff' : 'var(--ds-text)' }}>
                  Dynamic v2 (deterministic)
                </button>
                <button type="button" onClick={() => setEngine('v3')} disabled={extracting}
                  style={{ padding: '7px 16px', fontSize: 12, fontWeight: 700, border: 'none', cursor: 'pointer', fontFamily: 'inherit', outline: 'none', background: engine === 'v3' ? 'linear-gradient(135deg,#0041C2,#1A5FD4)' : 'var(--ds-input-bg)', color: engine === 'v3' ? '#fff' : 'var(--ds-text)' }}>
                  Dynamic v3 (skeleton)
                </button>
              </div>
              <div style={{ marginTop: 6, fontSize: 11, color: 'var(--ds-text2)' }}>
                Dynamic v2/v3 are 100% local &amp; deterministic — measured geometry, no LLM.
                v3 adds the header component, multi-page header repeat and key/value confidence.
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontWeight: 700, fontSize: 12, color: 'var(--ds-text)', marginBottom: 6 }}>Sample Company Resume</label>
              <input type="file" ref={sampleInputRef} onChange={handleSamplePick} accept={ACCEPT}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--ds-border)', background: 'var(--ds-input-bg)', color: 'var(--ds-text)', fontSize: 13, fontFamily: 'inherit', cursor: 'pointer', boxSizing: 'border-box', outline: 'none' }} />
              {sampleFile && (
                <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 8, background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <i className="fas fa-file-alt" style={{ color: '#16a34a', fontSize: 14 }} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 12, color: '#16a34a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sampleFile.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--ds-text2)' }}>{(sampleFile.size / 1024 / 1024).toFixed(2)} MB</div>
                    </div>
                  </div>
                  <button onClick={() => { setSampleFile(null); setDraft(null); if (sampleInputRef.current) sampleInputRef.current.value = ''; }} disabled={extracting} title="Remove selected file" style={{ padding: '3px 10px', borderRadius: 6, border: '1.5px solid #dc2626', background: 'transparent', color: '#dc2626', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', outline: 'none', flexShrink: 0 }}>
                    <i className="fas fa-times me-1" />Clear
                  </button>
                </div>
              )}
              <div style={{ marginTop: 8, fontSize: 11, color: 'var(--ds-text2)' }}>
                The system reverse-engineers this sample into a reusable template — typography, layout,
                section order, repeatable components and the company logo. Employee resumes are later
                re-rendered using <strong>only</strong> these rules.
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button onClick={handleExtract} disabled={!sampleFile || extracting}
                style={{ ...primaryBtn, opacity: !sampleFile || extracting ? 0.6 : 1 }}>
                {extracting
                  ? (<><span className="spinner-border spinner-border-sm" role="status" />&nbsp;Extracting template...</>)
                  : (<><i className="fas fa-magic" />Extract Template</>)}
              </button>
            </div>

            {extracting && (
              <div style={{ marginTop: 14, padding: '26px 18px', textAlign: 'center', background: 'var(--ds-input-bg)', border: '1px solid var(--ds-border)', borderRadius: 10 }}>
                <span className="spinner-border text-primary" role="status" style={{ width: 34, height: 34 }} />
                <div style={{ marginTop: 12, fontWeight: 700, fontSize: 13, color: 'var(--ds-text)' }}>Extracting template...</div>
                <div style={{ marginTop: 4, fontSize: 12, color: 'var(--ds-text2)' }}>
                  Reading layout &amp; typography, extracting assets, classifying sections. This can take up to a minute.
                </div>
              </div>
            )}

            {draft && !extracting && (
              <div style={{ marginTop: 16, border: '1px solid var(--ds-border)', borderRadius: 10, overflow: 'hidden' }}>
                <div style={{ padding: '12px 18px', borderBottom: '1px solid var(--ds-border)', display: 'flex', alignItems: 'center', gap: 10, background: 'var(--ds-input-bg)' }}>
                  <i className="fas fa-clipboard-check" style={{ color: '#16a34a', fontSize: 16 }} />
                  <div style={{ fontWeight: 800, fontSize: 14, color: 'var(--ds-text)' }}>Template Detected</div>
                  <span style={{ marginLeft: 'auto', fontSize: 10.5, padding: '2px 8px', borderRadius: 12, fontWeight: 700, background: (draft?.engine === 'v2' || draft?.engine === 'v3') ? 'rgba(0,65,194,0.12)' : 'var(--ds-card)', color: (draft?.engine === 'v2' || draft?.engine === 'v3') ? '#0041C2' : 'var(--ds-text2)', border: '1px solid var(--ds-border)' }}>
                    {draft?.engine === 'v3' ? 'dynamic v3' : draft?.engine === 'v2' ? 'dynamic v2' : 'classic v1'}
                  </span>
                  <span style={{ fontSize: 10.5, padding: '2px 8px', borderRadius: 12, fontWeight: 700, background: 'rgba(22,163,74,0.12)', color: '#16a34a', border: '1px solid rgba(22,163,74,0.35)' }}>
                    review before saving
                  </span>
                </div>
                <div style={{ padding: 16 }}>
                  <DetectedTemplateCard draft={draft} />

                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontWeight: 800, fontSize: 12.5, color: 'var(--ds-text)', marginBottom: 8 }}>
                      <i className="fas fa-eye me-2" style={{ color: '#1A5FD4', fontSize: 11 }} />Visual preview (rendered from the extracted template)
                    </div>
                    <iframe
                      key="tpl-draft-preview"
                      title="Template preview"
                      srcDoc={draft.preview_html}
                      sandbox="allow-same-origin allow-scripts"
                      style={{ width: '100%', height: 520, border: '1px solid var(--ds-border)', borderRadius: 8, background: '#fff' }}
                    />
                  </div>

                  <div style={{ marginTop: 16, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div style={{ flexGrow: 1, minWidth: 240 }}>
                      <label style={{ display: 'block', fontWeight: 700, fontSize: 12, color: 'var(--ds-text)', marginBottom: 6 }}>Template Name</label>
                      <input
                        value={templateName}
                        onChange={(e) => setTemplateName(e.target.value)}
                        placeholder="e.g. Company B Standard"
                        maxLength={120}
                        style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--ds-border)', background: 'var(--ds-input-bg)', color: 'var(--ds-text)', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' }}
                      />
                    </div>
                    <button onClick={handleSave} disabled={saving || !templateName.trim()}
                      style={{ ...primaryBtn, opacity: saving || !templateName.trim() ? 0.6 : 1, padding: '9px 20px' }}>
                      {saving
                        ? (<><span className="spinner-border spinner-border-sm" role="status" />&nbsp;Saving...</>)
                        : (<><i className="fas fa-save" />Approve &amp; Save Template</>)}
                    </button>
                  </div>
                  <div style={{ marginTop: 8, fontSize: 11, color: 'var(--ds-text2)' }}>
                    Saving keeps earlier versions — re-extracting with the same name creates a new version automatically.
                  </div>
                </div>
              </div>
            )}
          </>
        )}
    </>
  );

  if (embedded) {
    return (
      <div>
        <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--ds-text)', marginBottom: 6 }}>
          <i className="fas fa-building me-2" style={{ color: '#0041C2', fontSize: 11 }} />Your Company Templates
        </div>
        {renderBody()}
        {previewTemplate && (
          <CompanyTemplatePreviewModal
            template={previewTemplate}
            onClose={() => setPreviewTemplate(null)}
          />
        )}
      </div>
    );
  }

  return (
    <div style={{ background: 'var(--ds-card)', border: '1px solid var(--ds-border)', borderRadius: 12, boxShadow: 'var(--ds-shadow-md)' }}>
      <div style={{ padding: '14px 18px', background: 'linear-gradient(135deg,#0041C2,#1A5FD4)', display: 'flex', alignItems: 'center', gap: 12, borderTopLeftRadius: 12, borderTopRightRadius: 12 }}>
        <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <i className="fas fa-building" style={{ color: '#fff', fontSize: 14 }} />
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#fff' }}>Attach Your Own Resume Template</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.82)', marginTop: 2 }}>Pick one of your saved company templates, or extract a new one from a sample resume</div>
        </div>
      </div>
      <div style={{ padding: 18 }}>
        {renderBody()}
      </div>
      {previewTemplate && (
        <CompanyTemplatePreviewModal
          template={previewTemplate}
          onClose={() => setPreviewTemplate(null)}
        />
      )}
    </div>
  );
};

export default OwnTemplateFlow;
